import React from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Card,
    Checkbox,
    CircularProgress,
    IconButton,
    Link,
    Skeleton,
    Typography
} from '@mui/material';
import i18n from '../../../../i18n';
import Iconify from '../../../../components/iconify';
import {
    actionsButtonSx,
    cardFooterSx,
    cardHeaderSx,
    cardSx,
    cellSlotSx,
    clickableCardSx,
    headlineSx,
    identitySx,
    listHeaderSx,
    listSx,
    loadMoreButtonSx,
    metaSx,
    secondaryLabelSx,
    secondaryRowSx,
    secondaryValueSx
} from './GridMobileCards.styles';

// Mounts a grid cell, which renders its own <TableCell>; see cellSlotSx.
function CellSlot({ children, sx }) {
    return (
        <Box component="table" sx={[cellSlotSx, sx]}>
            <tbody>
                <tr>{children}</tr>
            </tbody>
        </Box>
    );
}

CellSlot.propTypes = {
    children: PropTypes.node,
    sx: PropTypes.oneOfType([PropTypes.object, PropTypes.func])
};

function CardSkeleton() {
    return (
        <Card sx={cardSx}>
            <Skeleton width="60%" height={22} />
            <Skeleton width="40%" height={18} />
            <Skeleton width="100%" height={30} />
        </Card>
    );
}

// The grid's table on a phone: a card per row, laid out by buildCardLayout.
// The grid renders each cell (`renderCell`), so formatting and permissions
// are those of the table.
export default function GridMobileCards({
    rows,
    layout,
    renderCell,
    loading = false,
    skeletonCount = 3,
    totalCount,
    selectionEnabled = false,
    selectMode = false,
    selected = [],
    onToggleSelect,
    onToggleSelectMode,
    onCardClick,
    onCardActions,
    hasActions,
    emptyContent = null,
    onLoadMore,
    hasMore = false,
    loadingMore = false
}) {
    const { identity, statuses, secondary, headline, meta } = layout;
    const hasCount = typeof totalCount === 'number' && totalCount >= 0;

    if (loading) {
        return (
            <Box sx={listSx}>
                {Array.from({ length: skeletonCount }, (_, index) => (
                    <CardSkeleton key={index} />
                ))}
            </Box>
        );
    }

    if (!rows.length) return emptyContent;

    const renderCard = (row) => {
        const isSelected = selected.includes(row.id);
        const showActions = !selectMode && hasActions(row.id);
        const clickable = selectMode || Boolean(onCardClick);

        // The event itself goes on, so the grid can tell a tap on a control
        // inside a cell (a switch, a copy button) from a tap on the card.
        const handleCardClick = (event) => {
            if (selectMode) onToggleSelect(row.id);
            else onCardClick?.(event, row.id);
        };

        return (
            <Card
                key={row.id}
                sx={[cardSx, clickable && clickableCardSx]}
                onClick={clickable ? handleCardClick : undefined}
            >
                <Box sx={cardHeaderSx}>
                    {selectMode && (
                        <Checkbox
                            checked={isSelected}
                            size="small"
                            sx={{ p: 0 }}
                        />
                    )}
                    {identity && (
                        <CellSlot sx={identitySx}>
                            {renderCell(identity, row)}
                        </CellSlot>
                    )}
                    {statuses.map((field) => (
                        <CellSlot key={field.id}>
                            {renderCell(field, row)}
                        </CellSlot>
                    ))}
                </Box>

                {secondary.length > 0 && (
                    <Box sx={secondaryRowSx}>
                        {secondary.map((field) => (
                            <Box key={field.id} sx={{ minWidth: 0 }}>
                                <Typography sx={secondaryLabelSx}>
                                    {i18n.t(`columns.${field.label}`)}
                                </Typography>
                                <CellSlot sx={secondaryValueSx}>
                                    {renderCell(field, row)}
                                </CellSlot>
                            </Box>
                        ))}
                    </Box>
                )}

                {(headline || meta || showActions) && (
                    <Box sx={cardFooterSx}>
                        {headline && (
                            <CellSlot sx={headlineSx}>
                                {renderCell(headline, row)}
                            </CellSlot>
                        )}
                        {meta && (
                            <CellSlot sx={metaSx}>
                                {renderCell(meta, row)}
                            </CellSlot>
                        )}
                        {showActions && (
                            <IconButton
                                sx={[actionsButtonSx, !meta && { ml: 'auto' }]}
                                aria-label={i18n.t('common.moreActions')}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onCardActions(event, row.id);
                                }}
                            >
                                <Iconify icon="eva:more-vertical-fill" />
                            </IconButton>
                        )}
                    </Box>
                )}
            </Card>
        );
    };

    return (
        <Box>
            <Box sx={listHeaderSx}>
                <Typography variant="caption" fontWeight={700}>
                    {hasCount
                        ? i18n.t('mobile.results', { count: totalCount })
                        : ''}
                </Typography>
                {selectionEnabled && (
                    <Link
                        component="button"
                        type="button"
                        variant="caption"
                        fontWeight={700}
                        underline="none"
                        onClick={onToggleSelectMode}
                    >
                        {selectMode
                            ? i18n.t('buttons.cancel')
                            : i18n.t('mobile.select')}
                    </Link>
                )}
            </Box>
            <Box sx={listSx}>
                {rows.map(renderCard)}
                {hasMore && (
                    <Button
                        variant="outlined"
                        sx={loadMoreButtonSx}
                        onClick={onLoadMore}
                        disabled={loadingMore}
                        startIcon={
                            loadingMore ? <CircularProgress size={16} /> : null
                        }
                    >
                        {hasCount
                            ? i18n.t('mobile.loadMoreOf', {
                                shown: rows.length,
                                total: totalCount
                            })
                            : i18n.t('mobile.loadMore')}
                    </Button>
                )}
            </Box>
        </Box>
    );
}

GridMobileCards.propTypes = {
    emptyContent: PropTypes.node,
    hasActions: PropTypes.func.isRequired,
    hasMore: PropTypes.bool,
    layout: PropTypes.shape({
        identity: PropTypes.object,
        statuses: PropTypes.array.isRequired,
        secondary: PropTypes.array.isRequired,
        headline: PropTypes.object,
        meta: PropTypes.object
    }).isRequired,
    loading: PropTypes.bool,
    loadingMore: PropTypes.bool,
    onCardActions: PropTypes.func.isRequired,
    onCardClick: PropTypes.func,
    onLoadMore: PropTypes.func.isRequired,
    onToggleSelect: PropTypes.func.isRequired,
    onToggleSelectMode: PropTypes.func.isRequired,
    renderCell: PropTypes.func.isRequired,
    rows: PropTypes.array.isRequired,
    selected: PropTypes.array,
    selectionEnabled: PropTypes.bool,
    selectMode: PropTypes.bool,
    skeletonCount: PropTypes.number,
    totalCount: PropTypes.number
};
